import {useEffect} from 'react';

import {
  publishWBAgentCrossTabEvent,
  subscribeWBAgentCrossTabEvents,
} from './crossTabSync';

export interface WBAgentTurnFinishedEvent {
  threadId: string;
  outcome: 'idle' | 'error' | 'cancelled';
}

type WBAgentTurnFinishedListener = (event: WBAgentTurnFinishedEvent) => void;

const listeners = new Set<WBAgentTurnFinishedListener>();

function emit(event: WBAgentTurnFinishedEvent): void {
  listeners.forEach(listener => {
    try {
      listener(event);
    } catch {
      // A lifecycle observer shouldn't disrupt turn settlement or other observers
    }
  });
}

export function notifyWBAgentTurnFinished(
  event: WBAgentTurnFinishedEvent
): void {
  // Fire in origin tab...
  emit(event);
  // ...and broadcast to other tabs
  publishWBAgentCrossTabEvent({
    type: 'turn-finished',
    threadId: event.threadId,
    outcome: event.outcome,
  });
}

export function subscribeWBAgentTurnFinished(
  onFinished: WBAgentTurnFinishedListener
): () => void {
  listeners.add(onFinished);
  ensureCrossTabBridge();
  return () => listeners.delete(onFinished);
}

export function useWBAgentTurnFinished(
  onFinished: WBAgentTurnFinishedListener
): void {
  useEffect(() => {
    return subscribeWBAgentTurnFinished(onFinished);
  }, [onFinished]);
}

// Forward `turn-finished` events from other tabs into the local bus so in-tab
// subscribers treat a remote settle exactly like a local one.
// Set up lazily on first subscribe (avoids SSR/import side effects) and left
// open for the tab's lifetime (ok because  emit() no-ops when
// nothing is subscribed).
let crossTabBridge: (() => void) | null = null;
function ensureCrossTabBridge(): void {
  if (crossTabBridge != null) {
    return;
  }
  crossTabBridge = subscribeWBAgentCrossTabEvents(event => {
    if (event.type === 'turn-finished') {
      emit({threadId: event.threadId, outcome: event.outcome});
    }
  });
}

export function __resetTurnLifeCycleForTests(): void {
  listeners.clear();
  crossTabBridge?.();
  crossTabBridge = null;
}
